import { ColumnsType } from 'antd/es/table';
import {
  Options,
  TooltipFormatterContextObject,
  TooltipOptions,
} from 'highcharts';
import { round } from 'lodash';
import moment from 'moment';
import { useMemo } from 'react';
import { DateType } from 'api/models';
import {
  TemplateInsightGroupBy,
  TemplateInsightResponses,
} from 'api/template-insight';
import {
  WidgetState,
  WidgetStateError,
} from 'components/organisms/projects-report/widgets';
import { numberOfWorkingDaysBetweenDates } from 'components/organisms/timesheets/common/utils';
import { DefaultWorkingDayConfig } from 'components/organisms/user-capacity';
import { useTranslate } from 'hooks/use-translation';
import { ChartZoomLevel, ZoomChartOption } from '../chart-with-zoom';
import { ChartTextStyle } from '../components';
import {
  TemplateInsightTooltip,
  TooltipStyle,
} from '../template-insight-tooltip';
import {
  CompareObjectType,
  DeviationMeta,
  Epoch,
  getLineChartOption,
} from '../utils';
import { PhaseRecord, usePhaseViewColumn } from './columns';

export type PhaseReports = Array<{
  template: {
    templateId: number;
    templateName: string;
  };
  templateName: string;
  phaseTemplateName: string;
  phaseTemplateId: number;
  plannedDuration: number;
  average: number;
  p50: number;
  p90: number;
  csat: number;
  deviation: number;
  phasesCompletedLate: number;
  startedOnTime: number;
  completedOnTime: number;
  totalPhases: number;
  averageStartingDelay: number;
}>;

const getTooltipFormatter =
  (tooltip: (val: number) => string) =>
  (
    option: TooltipFormatterContextObject,
    zoomLevel: ChartZoomLevel,
    deviationMetaByZoomLevel: Record<
      string,
      Record<ChartZoomLevel, Record<Epoch, DeviationMeta>>
    >,
  ) => {
    const epoch = option.point.x;
    const delayedBy = option.point.y;

    if (!epoch || delayedBy == null) {
      return '';
    }
    const timeFormatter =
      zoomLevel === ChartZoomLevel.Monthly ? 'MMM-YYYY' : 'DD-MMM';
    const time = moment(epoch).format(timeFormatter);
    const { name } = option.series;
    const deviationDetails = deviationMetaByZoomLevel[name][zoomLevel][epoch];

    if (!deviationDetails) {
      return '';
    }
    const noOfItems = deviationDetails.deviation.length;

    return TemplateInsightTooltip({
      title: tooltip(noOfItems),
      delayedBy,
      subTitle: name,
      time,
    });
  };

const calculateDuration = (phase: {
  startDateActual?: DateType;
  completedAt?: number;
}) => {
  if (!phase.startDateActual || !phase.completedAt) {
    return 0;
  }
  const startDateActual = moment(phase.startDateActual, 'YYYY-MM-DD');
  const endDate = moment(phase.completedAt);

  if (startDateActual > endDate) {
    return 0;
  }

  return numberOfWorkingDaysBetweenDates(
    startDateActual,
    endDate,
    DefaultWorkingDayConfig,
  );
};

const isStartedOnTime = (phase: {
  startDateActual?: DateType;
  startDate: DateType;
}) => {
  if (!phase.startDateActual) {
    return true;
  }
  const startDateActual = moment(phase.startDateActual, 'YYYY-MM-DD').valueOf();
  const startDate = moment(phase.startDate, 'YYYY-MM-DD').valueOf();

  return startDateActual - startDate <= 0;
};

const isCompletedOnTime = (phase: {
  dueDate: DateType;
  completedAt?: number;
}) => {
  if (!phase.completedAt) {
    return false;
  }
  const dueDate = moment(phase.dueDate, 'YYYY-MM-DD').valueOf();
  const { completedAt } = phase;

  return completedAt <= dueDate;
};

const calculateStartingDelay = (phase: {
  startDateActual?: DateType;
  startDate: DateType;
}) => {
  if (!phase.startDateActual) {
    return 1;
  }
  const startDateActual = moment(phase.startDateActual, 'YYYY-MM-DD');
  const startDate = moment(phase.startDate, 'YYYY-MM-DD');

  return startDateActual.diff(startDate, 'days');
};

export const usePhaseReports = ({
  templateInsight,
}: {
  templateInsight?: TemplateInsightResponses<TemplateInsightGroupBy.Phases>;
}): PhaseReports => {
  return useMemo(
    () =>
      templateInsight?.templateResponses.map((templateMeta) => {
        const {
          template,
          phaseTemplate,
          duration: plannedDuration,
          completionTime,
          averageCSAT,
          phasesCompletedLate,
          phases,
        } = templateMeta;
        const durations: Array<number> = [];
        let startedOnTime = 0;
        let completedOnTime = 0;
        const startingDelay: Array<number> = [];

        phases.forEach((phase) => {
          durations.push(calculateDuration(phase));
          if (isStartedOnTime(phase)) {
            startedOnTime += 1;
          }
          if (isCompletedOnTime(phase)) {
            completedOnTime += 1;
          }
          startingDelay.push(calculateStartingDelay(phase));
        });

        const averageDuration = round(
          durations.reduce((sum, a) => sum + a, 0) / durations.length,
        );
        const averageStartingDelay = round(
          startingDelay.reduce((sum, a) => sum + a, 0) / startingDelay.length,
        );
        const deviation = averageDuration - plannedDuration;

        return {
          template,
          templateName: template.templateName,
          phaseTemplateName: phaseTemplate.sourceName,
          phaseTemplateId: phaseTemplate.sourceId,
          phaseTemplateMeta: phaseTemplate,
          plannedDuration,
          average: averageDuration, // completionTime.averageDuration represents deviation and is calculated wrongly
          p50: completionTime.p50,
          p90: completionTime.p90,
          csat: averageCSAT ? round(averageCSAT, 1) : averageCSAT,
          phasesCompletedLate: round(phasesCompletedLate),
          deviation,
          startedOnTime,
          completedOnTime,
          totalPhases: phases.length,
          averageStartingDelay,
        };
      }) ?? [],
    [templateInsight?.templateResponses],
  );
};

export const usePhaseTable = ({
  phaseReport,
  isTemplateColumnRequired = true,
}: {
  phaseReport: PhaseReports;
  isTemplateColumnRequired?: boolean;
}): {
  tableColumn: ColumnsType<PhaseRecord>;
  tableData: {
    average: CompareObjectType;
    p50: CompareObjectType;
    p90: CompareObjectType;
    phasesCompletedLate: number;
    template: { templateId: number; templateName: string };
    templateName: string;
    phaseTemplateName: string;
    phaseTemplateId: number;
    plannedDuration: number;
    csat: number;
    deviation: number;
  }[];
} => {
  const tableColumn = usePhaseViewColumn({ isTemplateColumnRequired });

  return useMemo(() => {
    const tableData = phaseReport.map((record) => ({
      ...record,
      plannedDuration: record.plannedDuration,
      average: {
        value: Math.abs(record.average),
        cmpValue: record.plannedDuration - record.average,
      },
      p50: {
        value: Math.abs(record.p50),
        cmpValue: record.plannedDuration - record.p50,
      },
      p90: {
        value: Math.abs(record.p90),
        cmpValue: record.plannedDuration - record.p90,
      },
      phasesCompletedLate: record.phasesCompletedLate,
    }));

    return { tableColumn, tableData };
  }, [phaseReport, tableColumn]);
};

export const usePhaseWidget = ({
  phaseReport,
  isLoading,
}: {
  phaseReport: PhaseReports;
  isLoading: boolean;
}): {
  averageCompletionDelay: WidgetState<number, WidgetStateError>;
  percentageCompletedOnTime: WidgetState<number, WidgetStateError>;
  percentageStartedOnTime: WidgetState<number, WidgetStateError>;
  averageStartingDelay: WidgetState<number, WidgetStateError>;
} => {
  return useMemo(() => {
    if (isLoading) {
      return {
        averageCompletionDelay: WidgetState.loading(),
        percentageCompletedOnTime: WidgetState.loading(),
        percentageStartedOnTime: WidgetState.loading(),
        averageStartingDelay: WidgetState.loading(),
      };
    }

    const deviations = phaseReport.reduce(
      (acc, report) => acc + report.deviation,
      0,
    );
    const startingDelay = phaseReport.reduce(
      (acc, report) => acc + report.averageStartingDelay,
      0,
    );
    const averageCompletionDelay = round(deviations / phaseReport.length);
    const averageStartingDelay = round(startingDelay / phaseReport.length);
    let completedOnTime = 0;
    let startedOnTime = 0;
    let totalPhases = 0;

    phaseReport.forEach((report) => {
      startedOnTime += report.startedOnTime;
      completedOnTime += report.completedOnTime;
      totalPhases += report.totalPhases;
    });
    const percentageCompletedOnTime = round(
      (completedOnTime / totalPhases) * 100,
    );
    const percentageStartedOnTime = round((startedOnTime / totalPhases) * 100);

    const getWidgetData = (val?: number): WidgetState<number, never> =>
      val == null || Number.isNaN(val)
        ? WidgetState.noData()
        : WidgetState.data(val);

    return {
      averageCompletionDelay: getWidgetData(averageCompletionDelay),
      percentageCompletedOnTime: getWidgetData(percentageCompletedOnTime),
      percentageStartedOnTime: getWidgetData(percentageStartedOnTime),
      averageStartingDelay: getWidgetData(averageStartingDelay),
    };
  }, [isLoading, phaseReport]);
};

export const useLineChartForPhases = ({
  templateInsight,
}: {
  templateInsight?: TemplateInsightResponses<TemplateInsightGroupBy.Phases>;
}): ZoomChartOption => {
  const translate = useTranslate();

  return useMemo(() => {
    if (!templateInsight) {
      return { monthly: {}, weekly: {} };
    }
    const deviationMetaByZoomLevel = templateInsight.templateResponses.reduce<
      Record<string, Record<ChartZoomLevel, Record<Epoch, DeviationMeta>>>
    >((acc, record) => {
      record.phases.forEach((phase) => {
        if (!phase.startDateActual || !phase.completedAt) {
          return;
        }
        const startDateActual = moment(phase.startDateActual, 'YYYY-MM-DD');
        const startDatePlanned = moment(phase.startDate, 'YYYY-MM-DD');
        const completedAt = moment(phase.completedAt);
        const dueDate = moment(phase.dueDate, 'YYYY-MM-DD');
        const week = startDateActual.startOf('week').valueOf();
        const month = startDateActual.startOf('month').valueOf();
        const duration = completedAt.diff(startDateActual, 'days');
        const plannedDuration = dueDate.diff(startDatePlanned, 'days');
        const durationDiff = duration - plannedDuration;
        const phaseName = record.phaseTemplate.sourceName;

        if (!acc[phaseName]) {
          acc[phaseName] = { weekly: {}, monthly: {} };
        }
        const phaseAccumulator = acc[phaseName];
        const newWeekDeviation = (
          phaseAccumulator.weekly[week]?.deviation ?? []
        ).concat(durationDiff);
        const newMonthDeviation = (
          phaseAccumulator.monthly[month]?.deviation ?? []
        ).concat(durationDiff);
        const toSum = (sum: number, value: number) => sum + value;

        phaseAccumulator.weekly[week] = {
          deviation: newWeekDeviation,
          average: round(
            newWeekDeviation.reduce(toSum, 0) / newWeekDeviation.length,
          ),
        };
        phaseAccumulator.monthly[month] = {
          deviation: newMonthDeviation,
          average: round(
            newMonthDeviation.reduce(toSum, 0) / newMonthDeviation.length,
          ),
        };
      });

      return acc;
    }, {});

    return {
      monthly: getLineChartOption(
        ChartZoomLevel.Monthly,
        deviationMetaByZoomLevel,
        translate,
        {
          xAxisTitle: translate(
            'reports.template_insight.by-template.by-phase.chart.xAxis',
          ),
          showLegend: true,
          tooltipFormatter: getTooltipFormatter((val) =>
            translate('reports.template_insight.by-template.tooltip.phase', {
              count: val,
            }),
          ),
        },
      ),
      weekly: getLineChartOption(
        ChartZoomLevel.Weekly,
        deviationMetaByZoomLevel,
        translate,
        {
          xAxisTitle: translate(
            'reports.template_insight.by-template.by-phase.chart.xAxis',
          ),
          tooltipFormatter: getTooltipFormatter((val) =>
            translate('reports.template_insight.by-template.tooltip.phase', {
              count: val,
            }),
          ),
          showLegend: true,
        },
      ),
    };
  }, [templateInsight, translate]);
};

const getTooltipForBarChart = (isDelayed: boolean): TooltipOptions => ({
  formatter() {
    const getTitle = (idx) => this.series.data[idx].category;
    const title = getTitle(this.point.x);
    const delay = this.point.y ?? 0;
    const delayedBy = isDelayed ? delay : -1 * delay;

    if (!title || delayedBy == null) {
      return '';
    }

    return TemplateInsightTooltip({
      title,
      delayedBy,
    });
  },
  ...TooltipStyle,
});

export const useBarChartForPhases = (
  phaseReport: PhaseReports,
): { delayedPhaseOptions: Options; completedEarlyPhaseOptions: Options } => {
  const translate = useTranslate();

  return useMemo(() => {
    const accendOnDeviation = (
      a: { deviation: number },
      b: { deviation: number },
    ) => a.deviation - b.deviation;
    const decentOnDeviation = (
      a: { deviation: number },
      b: { deviation: number },
    ) => b.deviation - a.deviation;
    const delayedData = phaseReport
      .sort(decentOnDeviation)
      .filter(({ deviation }) => deviation > 0)
      .slice(0, 5);
    const earlyData = phaseReport
      .sort(accendOnDeviation)
      .filter((a) => a.deviation < 0)
      .slice(0, 5);

    const chartOption = {
      chart: {
        type: 'column',
      },
      yAxis: {
        min: 0,
        title: {
          text: translate(
            'reports.template_insight.overview.phases.delayed.chart.yAxisTitle',
          ),
        },
        ...ChartTextStyle,
      },
      plotOptions: {
        series: {
          allowPointSelect: true,
        },
      },
    };
    const delayedPhaseOptions: Options = {
      ...chartOption,
      series: [
        {
          data: delayedData.map(({ deviation }) => deviation),
          maxPointWidth: 31,
          color: 'var(--red-60)',
          type: 'column',
          name: 'Delayed Phase',
          showInLegend: false,
        },
      ],
      tooltip: getTooltipForBarChart(true),
      xAxis: {
        categories: delayedData.map(
          (data) => `${data.phaseTemplateName} ${data.templateName}`,
        ),
        ...ChartTextStyle,
      },
    };

    const completedEarlyPhaseOptions: Options = {
      ...chartOption,
      series: [
        {
          data: earlyData.map(({ deviation }) => -1 * deviation),
          maxPointWidth: 31,
          color: 'var(--green-40)',
          type: 'column',
          name: translate(
            'reports.template_insight.by-template.all_phases.chart.series',
          ),
          showInLegend: false,
        },
      ],
      tooltip: getTooltipForBarChart(false),
      xAxis: {
        categories: earlyData.map(
          (data) => `${data.phaseTemplateName} ${data.templateName}`,
        ),
        ...ChartTextStyle,
      },
    };

    return { delayedPhaseOptions, completedEarlyPhaseOptions };
  }, [phaseReport, translate]);
};
